import React, { useEffect, useState } from "react";

import "../../assets/styles/common-styles.scss";
import {
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";

import SitePage from "../Sites/SitePage";
import { AppState, useAppDispatch, useAppSelector } from "../../store";
import {
  setIsEditStudy,
  setOpenAddStudyModal,
  setOpenDeleteStudyDialog,
  setSelectedStudy,
  setStudyData,
} from "../../store/slices/studiesSlice";
import AddStudyModal from "./AddStudyModal";
import { useParams } from "react-router-dom";
import DeleteStudyDialog from "./DeleteStudyDialog";
import { useStudies } from "../../hooks/useStudies";
import { useSnackbar } from "../../hooks/useSnackbar";
import AssessmentPlans from "../AssessmentPlans";
import CentralLab from "../CentralLab";
import VendorsPage from "../Vendors";
import StudyDetails from "./StudyDetails";
import Enrollments from "../../pages/Enrollments";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`StudyDetails-tabpanel-${index}`}
      aria-labelledby={`StudyDetailsTab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `StudyDetails-${index}`,
    "aria-controls": `StudyDetails-${index}`,
  };
}

const StudyTabs: React.FunctionComponent<any> = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const { showError } = useSnackbar();
  const [value, setValue] = useState(0);
  const openDeleteStudyDialog = useAppSelector(
    (state: AppState) => state.studiesSlice.openDeleteStudyDialog
  );
  const [studyDetails, setStudyDetails] = useState<any>({});
  const openAddStudyModal = useAppSelector(
    (state: AppState) => state.studiesSlice.openAddStudyModal
  );
  const selectedStudy: any = params.study_id;
  const {
    getStudyDetails,
    studyDetailsData,
    studyDetailsIsError,
    studyDetailsIsLoading,
    studyDetailsIsSuccess,
    studyDetailsError,
  } = useStudies();
  useEffect(() => {
    getStudyDetails({ study_id: selectedStudy, is_audit: true });
  }, []);

  useEffect(() => {
    if (!studyDetailsIsLoading && studyDetailsIsSuccess) {
      setStudyDetails(studyDetailsData);
    }
  }, [studyDetailsData]);

  useEffect(() => {
    if (studyDetailsIsError) {
      const error: any = studyDetailsError;
      showError(error);
    }
  }, [studyDetailsError]);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const onEditStudyClick = () => {
    dispatch(setSelectedStudy(selectedStudy));
    dispatch(setStudyData(studyDetails));
    dispatch(setOpenAddStudyModal(true));
    dispatch(setIsEditStudy(true));
  };
  return (
    <>
      <Grid container spacing={2} mb={3}>
        <Grid item xs>
          <Stack direction="row" spacing={2}>
            <Typography variant="h4" gutterBottom>
              {studyDetails?.study_details?.study_id}
            </Typography>
            <Box>
              <Chip
                label={`IND ${studyDetails?.study_details?.ind ?? ""}`}
                sx={{ mt: 0.5 }}
              />
            </Box>
          </Stack>
        </Grid>
        {value === 0 && (
          <Grid item justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                dispatch(setSelectedStudy(selectedStudy));
                dispatch(setOpenDeleteStudyDialog(true));
              }}
              id="delete_study"
            >
              Delete
            </Button>
          </Grid>
        )}
      </Grid>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="Study Details Page Tabs for Sites/Central Lab/Assessment Plans/Vendors/Patients"
        variant="fullWidth"
        selectionFollowsFocus
      >
        <Tab label="Details" {...a11yProps(0)} />
        <Tab label="Sites" {...a11yProps(1)} />
        <Tab label="Central Lab" {...a11yProps(2)} />
        <Tab label="Vendors" {...a11yProps(3)} />
        <Tab label="Assessment Plans" {...a11yProps(4)} />
        <Tab label="Enrollment" {...a11yProps(5)} />
      </Tabs>
      <CustomTabPanel value={value} index={0}>
        <StudyDetails
          studyDetails={studyDetails}
          onEditStudyClick={onEditStudyClick}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <SitePage />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={2}>
        <CentralLab selectedStudy={selectedStudy} />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={3}>
        <VendorsPage />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <AssessmentPlans />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={5}>
        <Enrollments />
      </CustomTabPanel>
      {openAddStudyModal && <AddStudyModal />}
      {openDeleteStudyDialog && <DeleteStudyDialog />}
    </>
  );
};

export default StudyTabs;
